import { useAuiState } from "@assistant-ui/react";
import { useEffect } from "react";

import type { IAttachmentOption } from "@/api/attachments";
import {
    parseAuiAttachmentId,
    toAttachmentType,
} from "@/components/shared/user-message-attachment.utils";
import { setNextAttachments } from "@/pages/frontend/chat/adapters/api-thread-list-adapter";

/**
 * Stage the composer's attachments on the api-thread-list-adapter
 * side-channel so `initialize()` can forward them through
 * `POST /chat/conversations`. Pure pass-through; backend owns the
 * auto-tag rule. Mirrors the `pendingAgentId` pattern.
 */
export function useStageAttachments(): void {
    // Read the raw selector value (no `?? []` fallback at this level — a
    // fresh `[]` literal would change identity every render and re-fire
    // the effect endlessly). The empty/undefined case is handled inside
    // the effect.
    const attachments = useAuiState((s) => s.composer.attachments);
    useEffect(() => {
        if (!attachments || attachments.length === 0) {
            setNextAttachments(undefined);
            return;
        }
        const mapped: IAttachmentOption[] = attachments.map((a) => ({
            attachment_id: parseAuiAttachmentId(a.id),
            attachment_type: toAttachmentType(a.type),
            attachment_name: a.name ?? "",
            attachment_subtitle: "",
            ...(a.contentType === "kb" && { is_knowledge_base: true }),
        }));
        setNextAttachments(mapped);
    }, [attachments]);
}
